"use client";

import { useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";

// Reusable modal wrapper — no framer-motion to avoid compositor paint flash.
// Pure presentational component (only children + onModalClose props, no closure
// over component state), declared at module scope so React does not remount it.
// a11y: role=dialog/aria-modal, Escape to close, focus moves into the dialog on
// mount and is restored to the trigger on unmount. (Not a full focus trap — the
// demo modals are simple confirmations with a single action button.)
export function DemoModal({
  children,
  onModalClose,
}: {
  children: React.ReactNode;
  onModalClose?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onModalClose?.();
    };
    document.addEventListener("keydown", onKey);
    // Land keyboard focus inside the dialog; restore the trigger on close.
    ref.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus();
    };
  }, [onModalClose]);

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50" onClick={onModalClose} />
      <div className="fixed inset-0 z-[60] flex items-center justify-center px-5">
        <Card
          ref={ref}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          className="w-full max-w-sm gap-0 p-6 outline-hidden"
        >
          {children}
        </Card>
      </div>
    </>
  );
}
